import { cloneElement, isValidElement, useId } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { STATUS_LABEL } from "@/lib/format";

export const Logo = ({ className = "h-10" }) => (
  <img src="/qdb-logo.png" alt="QDB Consulting LLC" className={cn("w-auto object-contain", className)} data-testid="brand-logo" />
);

const STATUS_STYLE = {
  draft: "bg-amber-50 text-amber-800 border-amber-300",
  submitted: "bg-emerald-50 text-emerald-800 border-emerald-300",
  edited: "bg-sky-50 text-sky-800 border-sky-300",
  archived: "bg-slate-100 text-slate-600 border-slate-300",
  new: "bg-slate-100 text-slate-700 border-slate-300",
};

export const StatusBadge = ({ status, testId }) => (
  <span data-testid={testId} className={cn("inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider", STATUS_STYLE[status] || STATUS_STYLE.archived)}>
    {STATUS_LABEL[status] || status}
  </span>
);

export const StatCard = ({ label, value, icon: Icon, testId, hint }) => (
  <div className="panel relative overflow-hidden p-4 sm:p-5" data-testid={testId}>
    <div className="absolute left-0 top-0 h-full w-1 bg-[#5C241E]" />
    <div className="flex items-start justify-between gap-2">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">{label}</p>
      {Icon && <Icon className="h-4 w-4 text-[#5C241E]" />}
    </div>
    <p className="font-mono-num mt-2 text-3xl font-semibold text-slate-900" data-testid={testId ? `${testId}-value` : undefined}>{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

export const PageHeader = ({ eyebrow, title, actions, children }) => (
  <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {eyebrow && <p className="eyebrow mb-1">{eyebrow}</p>}
      <h1 className="text-3xl font-bold text-slate-900 sm:text-4xl" data-testid="page-title">{title}</h1>
      {children}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Field = ({ label, required, error, children, hint, htmlFor }) => {
  const auto = useId();
  const single = isValidElement(children) && typeof children.type !== "symbol";
  const id = htmlFor || (single && children.props.id) || auto;
  const child = single && !htmlFor && !children.props.id ? cloneElement(children, { id }) : children;
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={single || htmlFor ? id : undefined} className="block text-sm font-semibold text-slate-800">
          {label} {required && <span className="text-[#5C241E]">*</span>}
        </label>
      )}
      {child}
      {hint && !error && <p className="text-xs text-slate-600">{hint}</p>}
      {error && <p className="text-sm font-medium text-red-600" data-testid={`error-${htmlFor || "field"}`}>{error}</p>}
    </div>
  );
};

export const NativeSelect = ({ className, children, error, ...props }) => (
  <select {...props} className={cn("field-input appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%235C241E%22 stroke-width=%223%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-[length:14px] bg-[right_14px_center] bg-no-repeat pr-10", error && "border-red-500", className)}>
    {children}
  </select>
);

export const Spinner = ({ className }) => <Loader2 className={cn("h-5 w-5 animate-spin text-[#5C241E]", className)} />;

export const LoadingBlock = () => (
  <div className="flex min-h-[200px] items-center justify-center" data-testid="loading"><Spinner className="h-7 w-7" /></div>
);

export const EmptyState = ({ title, text, action, testId }) => (
  <div className="panel flex flex-col items-center justify-center px-6 py-12 text-center" data-testid={testId}>
    <p className="font-display text-xl font-semibold text-slate-900">{title}</p>
    {text && <p className="mt-1 max-w-sm text-sm text-slate-500">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);
